import get from 'lodash/get';
import React, { HTMLAttributes } from 'react';
import styled, { css } from 'styled-components';

export type FlexProps = HTMLAttributes<HTMLDivElement> & {
  direction?: 'row' | 'column' | 'row-reverse' | 'column-reverse';
  justify?: 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around';
  align?: 'flex-start' | 'flex-end' | 'center' | 'baseline' | 'stretch';
  wrap?: 'nowrap' | 'wrap' | 'wrap-reverse';
  flex?: number;
  gap?: number;
  grow?: number;
  shrink?: number;
  basis?: string;
  width?: string | number;
  height?: string | number;
  scroll?: 'x' | 'y' | 'both';
  ph?: number; // padding horizontal
  pv?: number; // padding vertical
  pt?: number; // padding top
  pb?: number; // padding bottom
  pl?: number; // padding left
  pr?: number; // padding right
  mt?: number; // margin top
  mb?: number; // margin bottom
  ml?: number; // margin left
  mr?: number; // margin right
  mh?: number; // margin horizontal
  mv?: number; // margin vertical
  bc?: string;
  bs?: 'solid' | 'dashed' | 'dotted';
  bw?: number;
  padding?: string;
  margin?: string;
  radius?: number | string;
  bg?: string;
  disabled?: boolean;
};

const StyledFlex = styled.div<FlexProps>`
  display: flex;
  flex-direction: ${({ direction }) => direction || 'row'};
  justify-content: ${({ justify }) => justify || 'flex-start'};
  align-items: ${({ align }) => align || 'flex-start'};
  flex-wrap: ${({ wrap }) => wrap || 'nowrap'};
  gap: ${({ gap }) => gap || 0}px;
  flex-basis: ${({ basis }) => basis || 'auto'};
  width: ${({ width }) => (typeof width === 'number' ? `${width}px` : width || '100%')};
  height: ${({ height }) => (typeof height === 'number' ? `${height}px` : height || 'auto')};
  overflow-x: ${({ scroll }) => (scroll === 'x' || scroll === 'both' ? 'auto' : 'visible')};
  overflow-y: ${({ scroll }) => (scroll === 'y' || scroll === 'both' ? 'auto' : 'visible')};
  padding: ${({ padding, ph, pv, pt, pb, pl, pr }) =>
    padding || `${pv || pt || 0}px ${ph || pr || 0}px ${pv || pb || 0}px ${ph || pl || 0}px`};
  margin: ${({ margin, mh, mv, mt, mb, ml, mr }) =>
    margin || `${mv || mt || 0}px ${mh || mr || 0}px ${mv || mb || 0}px ${mh || ml || 0}px`};
  border-radius: ${({ radius }) => (typeof radius === 'string' ? radius : `${radius || 0}px`)};
  background: ${({ bg, theme }) => get(theme.colors, bg) || bg || 'transparent'};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  border-color: ${({ bc, theme }) => get(theme.colors, bc) || bc || 'transparent'};
  border-style: ${({ bs }) => bs || 'solid'};
  border-width: ${({ bw }) => bw || 0}px;

  ${({ flex }) =>
    flex !== null &&
    css`
      flex: ${flex};
    `};

  ${({ grow }) =>
    grow !== null &&
    css`
      flex-grow: ${grow};
    `};

  ${({ shrink }) =>
    shrink !== null &&
    css`
      flex-shrink: ${shrink};
    `};
`;

export function Flex(props: FlexProps) {
  return <StyledFlex {...props} />;
}
